import { useEffect, useState } from "react";
import { api } from "../api.js";
import type { Announcement } from "./AnnouncementBanner.js";

const TYPE_LABEL: Record<string, string> = {
  update: "アップデート",
  maintenance: "メンテナンス",
  incident: "障害",
  news: "お知らせ",
};

const SESSION_KEY_PREFIX = "cs_toast_shown_";

/**
 * ダッシュボード初回マウント時、未読かつ `isImportant=true AND notifyBell=true`
 * のお知らせを画面右上に小さくトースト表示する。
 *
 * - `sessionStorage` で「このセッション中は1回だけ」を担保
 * - 閉じるボタン / 既読にするボタン
 */
export function AnnouncementToast() {
  const [items, setItems] = useState<Announcement[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getAnnouncements();
        const all: Announcement[] = r.announcements || [];
        const toShow = all.filter(
          (a) =>
            a.isImportant &&
            a.notifyBell &&
            !a.isRead &&
            !sessionStorage.getItem(SESSION_KEY_PREFIX + a.id),
        );
        // 表示対象をセッションストレージに記録
        toShow.forEach((a) => sessionStorage.setItem(SESSION_KEY_PREFIX + a.id, "1"));
        setItems(toShow);
      } catch {
        setItems([]);
      }
    })();
  }, []);

  const handleDismiss = (id: string) => {
    setItems((prev) => prev.filter((a) => a.id !== id));
  };

  const handleMarkRead = async (id: string) => {
    try {
      await api.markAnnouncementRead(id);
    } catch {}
    handleDismiss(id);
  };

  if (items.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        width: 340,
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {items.map((a) => (
        <div
          key={a.id}
          style={{
            background: "white",
            border: "2px solid #d32f2f",
            borderRadius: 10,
            padding: "12px 14px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            animation: "cs-toast-in 0.3s ease-out",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span
                  style={{
                    background: "#C62828",
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                >
                  {TYPE_LABEL[a.type]}
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: "#1a1a1a" }}>
                {a.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#5a2828",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {a.body.length > 80 ? a.body.slice(0, 80) + "…" : a.body}
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <button
                  onClick={() => handleMarkRead(a.id)}
                  style={{
                    background: "#C62828",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  既読にする
                </button>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(a.id)}
              style={{
                background: "transparent",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#6b6b68",
                padding: 0,
                lineHeight: 1,
                flexShrink: 0,
              }}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes cs-toast-in {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
